import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { bibliothequeService, Sourate, Verset } from '../services/bibliothequeService';

interface Reciter {
  code: string;
  name: string;
  name_ar: string;
}

interface FallbackSource {
  label: string;
  baseUrl: string;
  zeroPad?: boolean;
  note?: string;
}

const FALLBACK_RECITER_SOURCES: Record<string, FallbackSource> = {
  'ar.alijaber': {
    label: 'QuranicAudio.com',
    baseUrl: 'https://download.quranicaudio.com/quran/ali_jaber/{surah}.mp3',
    zeroPad: false,
    note: 'Source alternative (Ali Jaber)'
  },
};

const buildFallbackAudioUrl = (
  reciterCode: string,
  surahNumber: number
): { url: string; label: string; note?: string } | null => {
  const config = FALLBACK_RECITER_SOURCES[reciterCode];
  if (!config) {
    return null;
  }

  const formattedSurah = config.zeroPad === false
    ? `${surahNumber}`
    : surahNumber.toString().padStart(3, '0');

  return {
    url: config.baseUrl.replace('{surah}', formattedSurah),
    label: config.label,
    note: config.note,
  };
};

export const CoranDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [sourate, setSourate] = useState<Sourate | null>(null);
  const [versets, setVersets] = useState<Verset[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentAudio, setCurrentAudio] = useState<number | null>(null);
  const [reciters, setReciters] = useState<Reciter[]>([]);
  const [selectedReciter, setSelectedReciter] = useState<string>('ar.abdulbasitmurattal');
  const [selectedTranslation, setSelectedTranslation] = useState<string>('fr.hamidullah');
  const [loadingVersets, setLoadingVersets] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [sourateAudioUrl, setSourateAudioUrl] = useState<string | null>(null);
  const [currentAudioTime, setCurrentAudioTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [isPlayingFullSourate, setIsPlayingFullSourate] = useState(false);
  const [highlightedVerset, setHighlightedVerset] = useState<number | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [audioSource, setAudioSource] = useState<'primary' | 'fallback' | null>(null);
  const [fallbackSourceLabel, setFallbackSourceLabel] = useState<string | null>(null);
  const [fallbackSourceNote, setFallbackSourceNote] = useState<string | null>(null);
  const sourateAudioRef = useRef<HTMLAudioElement>(null);
  const versetRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const lastScrollTime = useRef<number>(0);
  const lastVersetId = useRef<number | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fallbackAttemptRef = useRef(false);

  useEffect(() => {
    if (id) {
      loadSourate();
      loadReciters();
      setInitialLoad(true);
    }
  }, [id]);

  useEffect(() => {
    if (id && reciters.length > 0 && sourate && initialLoad) {
      // Charger les versets après le chargement initial des récitateurs
      loadVersets();
      setInitialLoad(false);
    }
  }, [reciters.length, sourate, initialLoad, id]);

  useEffect(() => {
    if (id && reciters.length > 0 && sourate && !initialLoad) {
      // Recharger les versets quand le récitateur ou la traduction change (sauf au premier chargement)
      setCurrentAudio(null); // Arrêter l'audio en cours
      setAudioError(null); // Réinitialiser l'erreur audio
      loadVersets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReciter, selectedTranslation]);

  const loadSourate = async () => {
    if (!id) return;
    try {
      const data = await bibliothequeService.getSourateById(parseInt(id));
      setSourate(data);
    } catch (error) {
      console.error('Erreur:', error);
      navigate('/bibliotheque');
    } finally {
      setLoading(false);
    }
  };

  const loadReciters = async () => {
    try {
      const data = await bibliothequeService.getAvailableReciters();
      setReciters(data);
      if (data.length > 0 && !selectedReciter) {
        setSelectedReciter(data[0].code);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des récitateurs:', error);
    }
  };

  const loadVersets = async () => {
    if (!id || !sourate) return;
    setLoadingVersets(true);
    fallbackAttemptRef.current = false;
    setAudioSource(null);
    setFallbackSourceLabel(null);
    setFallbackSourceNote(null);
    try {
      // Utiliser l'API avec le récitateur et la traduction sélectionnés
      const data = await bibliothequeService.getVersetsBySourate(
        parseInt(id), 
        true, 
        selectedTranslation, 
        selectedReciter
      );
      setVersets(data);
      setCurrentAudio(null); // Réinitialiser l'audio en cours
      
      // Obtenir l'URL audio de la sourate complète via l'API
      try {
        const audioUrl = await bibliothequeService.getSourateAudioUrl(parseInt(id), selectedReciter);
        setSourateAudioUrl(audioUrl);
        setAudioError(null); // Réinitialiser l'erreur
        setAudioSource('primary');
        fallbackAttemptRef.current = false;
      } catch (error: any) {
        console.error('Erreur lors de la récupération de l\'URL audio:', error);
        // Si le récitateur n'est pas disponible, afficher un message d'erreur
        if (error.response?.status === 404) {
          const fallbackData = buildFallbackAudioUrl(selectedReciter, sourate.numero);
          if (fallbackData) {
            setSourateAudioUrl(fallbackData.url);
            setAudioSource('fallback');
            fallbackAttemptRef.current = true;
            setFallbackSourceLabel(fallbackData.label);
            setFallbackSourceNote(fallbackData.note || null);
            setAudioError(null);
          } else {
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Ce récitateur n\'est pas disponible.';
            setAudioError(errorMessage);
            setSourateAudioUrl(null); // Ne pas essayer de charger l'audio
          }
        } else {
          // Fallback: essayer avec l'URL CDN directe
          const fallbackUrl = `https://cdn.islamic.network/quran/audio-surah/128/${selectedReciter}/${sourate.numero}.mp3`;
          setSourateAudioUrl(fallbackUrl);
          setAudioError(null); // On laisse l'erreur se déclencher si l'URL ne fonctionne pas
          setAudioSource('primary');
          fallbackAttemptRef.current = false;
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des versets:', error);
      // Fallback : essayer sans API
      try {
        const fallbackData = await bibliothequeService.getVersetsBySourate(parseInt(id), false);
        setVersets(fallbackData);
        // Essayer d'obtenir l'URL audio de la sourate
        if (sourate) {
          try {
            const audioUrl = await bibliothequeService.getSourateAudioUrl(parseInt(id), selectedReciter);
            setSourateAudioUrl(audioUrl);
            setAudioError(null);
            setAudioSource('primary');
            fallbackAttemptRef.current = false;
          } catch (error) {
            console.error('Erreur lors de la récupération de l\'URL audio (fallback):', error);
            const fallbackData = buildFallbackAudioUrl(selectedReciter, sourate.numero);
            if (fallbackData) {
              setSourateAudioUrl(fallbackData.url);
              setAudioSource('fallback');
              fallbackAttemptRef.current = true;
              setFallbackSourceLabel(fallbackData.label);
              setFallbackSourceNote(fallbackData.note || null);
              setAudioError(null);
            } else {
              const fallbackUrl = `https://cdn.islamic.network/quran/audio-surah/128/${selectedReciter}/${sourate.numero}.mp3`;
              setSourateAudioUrl(fallbackUrl);
              setAudioSource('primary');
              fallbackAttemptRef.current = false;
            }
          }
        }
      } catch (fallbackError) {
        console.error('Erreur fallback:', fallbackError);
      }
    } finally {
      setLoadingVersets(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-20 w-20 border-4 border-green-200 border-t-green-700"></div>
      </div>
    );
  }

  if (!sourate) {
    return null;
  }

  return (
    <div className="min-h-screen relative bg-gradient-to-br from-white via-green-50/30 to-green-100/50 overflow-hidden">
      {/* Motifs décoratifs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-green-200/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-green-300/20 rounded-full blur-3xl"></div>
      </div>

      {/* Hero Section */}
      <section className="relative z-10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/bibliotheque"
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-green-700 mb-6 transition-colors"
          >
            <span>←</span>
            <span>Retour à la bibliothèque</span>
          </Link>

          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-green-100/50 overflow-hidden mb-8">
            {/* Header */}
            <div className="relative h-64 bg-gradient-to-br from-green-500 via-green-600 to-green-700 overflow-hidden">
              <div 
                className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage: `url('data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')`
                }}
              ></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-8">
                <div className="text-8xl mb-4 opacity-80">📿</div>
                <div className="text-6xl font-bold mb-2">{sourate.numero}</div>
                <h1 className="text-4xl sm:text-5xl font-bold mb-3 text-center">{sourate.nom_francais}</h1>
                <p className="text-3xl text-right" dir="rtl">{sourate.nom_arabe}</p>
              </div>
            </div>

            {/* Informations et sélecteurs */}
            <div className="p-8">
              {/* Sélecteurs de récitateur et traduction */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 mb-6 border border-green-200/50">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <span>⚙️</span>
                  <span>Options de lecture</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      🎤 Récitateur (Audio)
                    </label>
                    <select
                      value={selectedReciter}
                      onChange={(e) => setSelectedReciter(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white font-medium"
                    >
                      {reciters.map((reciter) => (
                        <option key={reciter.code} value={reciter.code}>
                          {reciter.name} ({reciter.name_ar})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      📖 Traduction
                    </label>
                    <select
                      value={selectedTranslation}
                      onChange={(e) => setSelectedTranslation(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white font-medium"
                    >
                      <option value="fr.hamidullah">Français - Hamidullah</option>
                      <option value="fr.rashid">Français - Rashid Maach</option>
                      <option value="en.ahmedali">English - Ahmed Ali</option>
                      <option value="en.sahih">English - Sahih International</option>
                    </select>
                  </div>
                </div>
                {loadingVersets && (
                  <div className="mt-4 text-center">
                    <span className="inline-flex items-center space-x-2 text-green-700">
                      <span className="animate-spin">⏳</span>
                      <span>Chargement des versets avec les nouvelles options...</span>
                    </span>
                  </div>
                )}
              </div>

              {/* Audio complet de la sourate */}
              {sourateAudioUrl && (
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 mb-6 border border-purple-200/50">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                    <span>🎧</span>
                    <span>Écouter la sourate complète</span>
                    {isPlayingFullSourate && (
                      <span className="ml-2 px-3 py-1 bg-purple-600 text-white text-xs rounded-full animate-pulse">
                        En cours de lecture...
                      </span>
                    )}
                  </h3>
                  {audioError && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-700 text-sm font-semibold">⚠️ {audioError}</p>
                      <p className="text-red-600 text-xs mt-1">
                        Ce récitateur n'est pas disponible sur cette source d'audio. 
                        Veuillez sélectionner un autre récitateur dans les options ci-dessus.
                      </p>
                      <p className="text-red-500 text-xs mt-2 font-medium">
                        💡 Récommandé: Abdul Basit Murattal, Mishary Rashid Alafasy, ou Mahmoud Khalil Al-Husary
                      </p>
                    </div>
                  )}
                  {audioSource === 'fallback' && fallbackSourceLabel && (
                    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-blue-800 text-sm font-semibold">
                        🔁 Lecture via source alternative : {fallbackSourceLabel}
                      </p>
                      {fallbackSourceNote && (
                        <p className="text-blue-700 text-xs mt-1">{fallbackSourceNote}</p>
                      )}
                      <p className="text-blue-600 text-xs mt-2">
                        Si cette lecture présente des erreurs, essayez un autre récitateur disponible ci-dessus.
                      </p>
                    </div>
                  )}
                  <audio
                    ref={sourateAudioRef}
                    key={`${selectedReciter}-${sourate?.numero}`} // Forcer le rechargement si le récitateur change
                    src={sourateAudioUrl}
                    controls
                    preload="auto"
                    className="w-full"
                    onTimeUpdate={(e) => {
                      const audio = e.currentTarget;
                      const currentTime = audio.currentTime;
                      setCurrentAudioTime(currentTime);
                      
                      // Calculer quel verset correspond au temps actuel avec synchronisation améliorée
                      if (audioDuration > 0 && versets.length > 0 && isPlayingFullSourate) {
                        // Utiliser une approche plus conservatrice pour éviter d'être en avance
                        // On calcule le verset basé sur le temps réel avec un léger retard pour la stabilité
                        const progress = Math.min(currentTime / audioDuration, 0.999); // Éviter 1.0 exact
                        
                        // Calculer l'index du verset avec un offset conservateur
                        // On utilise un offset négatif pour être légèrement en retard sur l'audio
                        // Ajuster dynamiquement selon la durée totale pour plus de précision
                        const offset = -Math.max(0.02, audioDuration * 0.001); // Minimum 2% ou 0.1% de la durée
                        const adjustedProgress = Math.max(0, progress + offset);
                        
                        let estimatedVersetIndex = Math.floor(adjustedProgress * versets.length);
                        
                        // S'assurer que l'index est valide
                        estimatedVersetIndex = Math.max(0, Math.min(estimatedVersetIndex, versets.length - 1));
                        
                        const currentVerset = versets[estimatedVersetIndex];
                        
                        // Ne mettre à jour que si le verset a vraiment changé
                        if (currentVerset && currentVerset.id !== lastVersetId.current) {
                          lastVersetId.current = currentVerset.id;
                          setHighlightedVerset(currentVerset.id);
                          
                          // Faire défiler seulement si on n'a pas scrollé récemment (évite trop de scrolls)
                          const now = Date.now();
                          if (now - lastScrollTime.current > 1000) { // Augmenté à 1 seconde pour plus de stabilité
                            lastScrollTime.current = now;
                            
                            // Annuler le scroll précédent s'il existe
                            if (scrollTimeoutRef.current) {
                              clearTimeout(scrollTimeoutRef.current);
                            }
                            
                            // Délai pour éviter les scrolls trop fréquents
                            scrollTimeoutRef.current = setTimeout(() => {
                              const versetElement = versetRefs.current[currentVerset.id];
                              if (versetElement && isPlayingFullSourate) {
                                // Vérifier si le verset est déjà visible dans la viewport
                                const rect = versetElement.getBoundingClientRect();
                                const isVisible = rect.top >= 0 && rect.top <= window.innerHeight * 0.7;
                                
                                // Scroller seulement si le verset n'est pas déjà visible
                                if (!isVisible) {
                                  versetElement.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center'
                                  });
                                }
                              }
                            }, 400); // Augmenté le délai pour plus de stabilité
                          }
                        }
                      }
                    }}
                    onLoadedMetadata={(e) => {
                      const audio = e.currentTarget;
                      setAudioDuration(audio.duration);
                    }}
                    onPlay={() => {
                      setIsPlayingFullSourate(true);
                    }}
                    onPause={() => {
                      setIsPlayingFullSourate(false);
                      // Garder le verset en surbrillance même en pause
                    }}
                    onEnded={() => {
                      setIsPlayingFullSourate(false);
                      setHighlightedVerset(null);
                      setCurrentAudioTime(0);
                      lastVersetId.current = null;
                      if (scrollTimeoutRef.current) {
                        clearTimeout(scrollTimeoutRef.current);
                      }
                    }}
                    onError={(e) => {
                      const audioEl = e.currentTarget;
                      console.error('Erreur de chargement audio sourate:', {
                        error: e,
                        src: sourateAudioUrl,
                        errorCode: audioEl.error?.code,
                        errorMessage: audioEl.error?.message
                      });
                      // Afficher un message d'erreur à l'utilisateur
                      if (audioEl.error) {
                        if (!fallbackAttemptRef.current) {
                          const fallbackData = sourate ? buildFallbackAudioUrl(selectedReciter, sourate.numero) : null;
                          if (fallbackData) {
                            fallbackAttemptRef.current = true;
                            setAudioSource('fallback');
                            setFallbackSourceLabel(fallbackData.label);
                            setFallbackSourceNote(fallbackData.note || null);
                            setAudioError(null);
                            setSourateAudioUrl(fallbackData.url);
                            return;
                          }
                        }
                        
                        const errorCode = audioEl.error?.code;
                        let errorMessage = "L'audio pour ce récitateur n'est pas disponible.";
                        
                        if (errorCode === 4) { // MEDIA_ELEMENT_ERROR: Format error
                          errorMessage = "Ce récitateur n'est pas disponible sur cette source d'audio.";
                        } else if (errorCode === 2) { // NETWORK_ERROR
                          errorMessage = "Erreur de réseau lors du chargement de l'audio.";
                        } else if (errorCode === 3) { // DECODE_ERROR
                          errorMessage = "Erreur de décodage de l'audio. Ce récitateur n'est peut-être pas disponible.";
                        }
                        
                        setAudioError(errorMessage);
                        setIsPlayingFullSourate(false);
                      }
                    }}
                    onLoadedData={() => {
                      console.log('Audio sourate chargé avec succès:', sourateAudioUrl);
                      setAudioError(null); // Effacer l'erreur si le chargement réussit
                    }}
                  >
                    Votre navigateur ne supporte pas la lecture audio.
                  </audio>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border border-green-200/50">
                  <div className="text-3xl mb-2">📖</div>
                  <h3 className="font-semibold text-gray-700 mb-1">Nombre de versets</h3>
                  <p className="text-2xl font-bold text-green-900">{sourate.nombre_versets}</p>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl p-6 border border-amber-200/50">
                  <div className="text-3xl mb-2">📌</div>
                  <h3 className="font-semibold text-gray-700 mb-1">Type</h3>
                  <p className="text-xl font-bold text-amber-900">{sourate.type_sourate}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200/50">
                  <div className="text-3xl mb-2">🎯</div>
                  <h3 className="font-semibold text-gray-700 mb-1">Sourate n°</h3>
                  <p className="text-2xl font-bold text-blue-900">{sourate.numero}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Versets */}
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-green-100/50 overflow-hidden">
            <div className="p-8 border-b border-gray-200">
              <h2 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
                <span>📜</span>
                <span>Versets ({versets.length})</span>
              </h2>
            </div>

            <div className="p-8">
              {versets.length > 0 ? (
                <div className="space-y-6">
                  {versets.map((verset, index) => (
                    <div
                      key={verset.id}
                      ref={(el) => {
                        versetRefs.current[verset.id] = el;
                      }}
                      className={`rounded-2xl p-6 border transition-all duration-300 animate-slide-up ${
                        highlightedVerset === verset.id
                          ? 'bg-gradient-to-br from-purple-100 to-purple-200 border-purple-400 shadow-xl scale-[1.02] ring-2 ring-purple-500'
                          : 'bg-gradient-to-br from-gray-50 to-white border-gray-200/50 hover:shadow-lg'
                      }`}
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <span className={`w-10 h-10 rounded-full flex items-center justify-center font-bold shadow-lg transition-all duration-300 ${
                            highlightedVerset === verset.id
                              ? 'bg-gradient-to-br from-purple-600 to-purple-700 text-white scale-110 animate-pulse'
                              : 'bg-gradient-to-br from-green-500 to-green-600 text-white'
                          }`}>
                            {verset.numero}
                          </span>
                          <span className="text-gray-500 text-sm">Verset {verset.numero}</span>
                          {highlightedVerset === verset.id && isPlayingFullSourate && (
                            <span className="px-2 py-1 bg-purple-600 text-white text-xs rounded-full animate-pulse">
                              En lecture
                            </span>
                          )}
                        </div>
                        {verset.audio && (
                          <button
                            onClick={() => {
                              // Si l'audio général est en cours, l'arrêter d'abord
                              if (isPlayingFullSourate && sourateAudioRef.current) {
                                sourateAudioRef.current.pause();
                                setIsPlayingFullSourate(false);
                              }
                              setCurrentAudio(currentAudio === verset.id ? null : verset.id);
                            }}
                            className="w-10 h-10 bg-green-600 hover:bg-green-700 text-white rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-110 shadow-lg"
                          >
                            {currentAudio === verset.id ? '⏸️' : '▶️'}
                          </button>
                        )}
                      </div>

                      <div className="space-y-4">
                        {/* Texte arabe */}
                        <div className="bg-white rounded-xl p-6 border border-gray-200">
                          <p className="text-3xl leading-relaxed text-right" dir="rtl">
                            {verset.texte_arabe}
                          </p>
                        </div>

                        {/* Traduction */}
                        {verset.traduction_francais && (
                          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
                            <p className="text-lg leading-relaxed text-gray-800">
                              {verset.traduction_francais}
                            </p>
                          </div>
                        )}

                        {/* Audio */}
                        {verset.audio && currentAudio === verset.id && (
                          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                            <div className="mb-2 text-sm text-gray-600">
                              📡 {verset.audio.includes('islamic.network') ? 'Audio via Al-Quran Cloud' : 'Audio local'}
                            </div>
                            <audio
                              key={verset.audio} // Forcer le rechargement si l'URL change
                              src={verset.audio}
                              controls
                              preload="auto"
                              className="w-full"
                              onEnded={() => setCurrentAudio(null)}
                              onError={(e) => {
                                const audioEl = e.currentTarget;
                                console.error('Erreur de chargement audio:', {
                                  error: e,
                                  src: verset.audio,
                                  errorCode: audioEl.error?.code,
                                  errorMessage: audioEl.error?.message
                                });
                                // Ne pas afficher d'alerte à chaque erreur, juste logger
                              }}
                              onCanPlay={(e) => {
                                // Forcer la lecture si possible
                                const audio = e.currentTarget;
                                const playPromise = audio.play();
                                if (playPromise !== undefined) {
                                  playPromise.catch((error) => {
                                    console.error('Erreur de lecture audio (autoplay bloqué par le navigateur):', error);
                                    // Ne pas afficher d'erreur si c'est juste l'autoplay bloqué
                                  });
                                }
                              }}
                              onLoadedData={() => {
                                console.log('Audio chargé avec succès:', verset.audio);
                              }}
                            >
                              Votre navigateur ne supporte pas la lecture audio.
                            </audio>
                            {verset.audio && (
                              <div className="mt-2">
                                <a
                                  href={verset.audio}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                                >
                                  🔗 Ouvrir l'audio dans un nouvel onglet
                                </a>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="inline-block bg-white/90 backdrop-blur-sm rounded-3xl p-12 shadow-xl border border-green-100/50">
                    <div className="text-8xl mb-6">📜</div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">Aucun verset disponible</h3>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

